


import { IFetcher } from '../interface/util'
import { IConfigAPI, IConfigCache, injectConfigAPI, injectConfigCache } from '../interface/config';
import DexieDB from "../private/db/private/db";
import { ISession, ISessionOptions } from "../interface/session";
import authManager from "../private/manager/auth";
import mediaManager from "../private/manager/media";
import userState from "../private/manager/userState";
import operationManager from "../private/manager/operation";
import { IAuthenticationManager } from "../interface/authentication";
import { IWorkspaceDB } from "../interface/db";
import ContainerDB from "../private/db/db";
import { IAuthManager } from '../private/manager/auth.if';

const TABLE_NAME = "ISession";
const TABLE_OPTIONS = "name, grouping, languageId, tourId, auth"

export interface PrivSaveData {
    name: string;
    auth: string;
}

function _db(options: ISessionOptions = {}): DexieDB {
    let db = new DexieDB({
        version: 1,
        mappings: {
            [TABLE_NAME]: TABLE_OPTIONS,
        },
        name: options.name ?? TABLE_NAME,
    });

    return db;
}

function _toPrivate(session: ISession) : PrivSaveData {
    return {
        name: session.name,
        auth: (session.auth as IAuthManager).serialize() ?? ''
    }
}

function _authManager(): IAuthenticationManager {
    const config = injectConfigAPI();

    const auth = authManager({
        apiUrl : config.url, 
        apiExtension: config.extension.auth,
        clientId : config.auth.clientId, 
        clientSecret: config.auth.clientSecret,
        grantType: config.auth.grantType,
        username: config.auth.username,
        password: config.auth.password,
    })

    return auth;
}

function _workspaceDB(name: string): IWorkspaceDB {
    return new ContainerDB({
        name,
    });
}

function _toSession(data: PrivSaveData, options: ISessionOptions = {}) : ISession {
    const configAPI = injectConfigAPI();
    const configCache = injectConfigCache();

    const auth = authManager(data.auth);
    const db = _workspaceDB(data.name);

    const media = mediaManager(db, auth, configAPI, configCache);
    const operation = operationManager(auth, media, db, data.name);
    const state = userState(data.name);

    const save = function(this: ISession) { _save(this as ISession, options) }
    const remove = function(this: ISession) { _remove(data.name, options) }

    const session: ISession = {
        name: data.name,

        auth,
        db,
        media,
        operation,
        state,

        save,
        remove,
    }
    return session;
}

function _load(name: string, options: ISessionOptions = {}): Promise<ISession> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db(options);

            await db.open();

            const table = db.table(TABLE_NAME);
            const result: PrivSaveData = await table.get(name);

            db.close();
            if ( result ) {
                const loadData = _toSession(result);
                resolve(loadData)
            } else {
                reject('Unable to find session named: ' + name)
            }            
        } catch (error) {
            reject(error);
        }
    });
}

function _save(session: ISession, options: ISessionOptions = {}) : Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db(options);

            await db.open();

            const table = db.table(TABLE_NAME);

            const saveObject = _toPrivate(session);

            const result = await table.put(saveObject);

            db.close();

            ( result === session.name ) ?
                resolve() :
                reject('Failed to save data into patch-storage table');
        } catch (error) {
            reject(error);
        }
    });
}

function _remove(name: string, options: ISessionOptions = {}): Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db(options);

            await db.open();

            const table = db.table(TABLE_NAME);

            await table.delete(name);

            db.close();

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

function session(name: string, options: ISessionOptions = {}): Promise<ISession> {
    return new Promise(async function(resolve, reject) {
        try {
            let session: ISession|null = null;

            try {
                session = await _load(name, options);
            } catch (error) {
                /* ignore */
            }

            if ( !session ) {
                session = _toSession({
                    name: name,
                    auth: (_authManager() as IAuthManager).serialize(),
                })
            }
        
            resolve(session);            
        } catch (error) {
            reject(error);
        }
    })
}

export default session;